<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import api from '@/api';
import { ContentsContext, Feature } from '@/api/tracking';
import noImage1to1 from '@/assets/noimage-1to1.jpg';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import DocumentSentenceDisplay from '@/components/common/adp-document/document-sentence-display.vue';
import UserDocumentOpenPreviewModalButton from '@/components/user-document/user-document-open-preview-modal-button.vue';
import UserDocumentSimilarDocumentCard from '@/components/user-document/user-document-similar-document-card.vue';
import {
  UserDocument,
  UserDocumentEventName,
  UserDocumentViewInfo,
  UserDocumentViewType,
} from '@/types';
import { getHighlightKeywords } from '@/utils/adpDocument';
import { useUserDocumentActionHistories } from '@/utils/composables/useUserDocumentActionHistories';
import { imageErrorHandler } from '@/utils/errorHandler';
import {
  getDocumentUrl,
  getFileTypeIconName,
  getFolderName,
  getModifiedAt,
  getStorageType,
} from '@/utils/user-document/common';
import { userSession } from '@/utils/userSession';
import { isDev } from '@/featureFlags';

interface Props {
  userDocument: UserDocument;
  rankInWholeFeed?: number;
  feature?: Feature;
  contentsContext: ContentsContext;
}

const SIMILAR_DOCUMENTS_INCREMENT_NUMBER = 10;
const SIMILAR_DOCUMENTS_INITIAL_NUMBER = 3;

const props = defineProps<Props>();
const emit = defineEmits<{
  'select-file': [id: string];
}>();
const route = useRoute();
const imageUrl = computed(() => props.userDocument.image_url || noImage1to1);
const fileTypeIcon = computed(() =>
  getFileTypeIconName(props.userDocument.file_type),
);
const folderName = computed(() => getFolderName(props.userDocument));
const documentUrl = computed(() => getDocumentUrl(props.userDocument));
const modifiedAt = computed(() => getModifiedAt(props.userDocument));

const highlightKeywords = computed(() =>
  getHighlightKeywords(props.userDocument),
);

const { createUserDocumentViewHistories } = useUserDocumentActionHistories(
  userSession.getUserId(),
);

const clickUserDocumentTitle = async () => {
  await createUserDocumentViewHistories(props.userDocument.id);
  await api.trackingUserDocumentEvent(
    'user_document_view',
    userDocumentViewInfo('original'),
  );
};

const trackingUserDocumentEvent = async (eventName: UserDocumentEventName) =>
  await api.trackingUserDocumentEvent(eventName, {
    page: {
      name: 'user_document_search',
    },
    feature: 'user_document_search',
    rank: props.rankInWholeFeed ?? undefined,
    user_document: {
      id: props.userDocument.id,
    },
    contents_context: props.contentsContext as Extract<
      ContentsContext,
      { event_name: 'user_document_search' }
    >,
    view_type: 'original',
  } as UserDocumentViewInfo);

const trackingSimilarUserDocumentsShowMoreEvent = async () =>
  await api.trackingSimilarUserDocumentsShowMoreEvent(
    'similar_user_documents_show_more',
    {
      page: {
        name: 'user_document_search',
      },
      feature: 'user_document_search',
      rank: props.rankInWholeFeed ?? undefined,
      contents_context: props.contentsContext as Extract<
        ContentsContext,
        { event_name: 'user_document_search' }
      >,
    },
  );

const showMoreSimilarDocuments = () => {
  displaySimilarDocumentNumber.value = Math.min(
    props.userDocument.similar_documents?.length ?? 0,
    displaySimilarDocumentNumber.value + SIMILAR_DOCUMENTS_INCREMENT_NUMBER,
  );
  trackingSimilarUserDocumentsShowMoreEvent();
};

const displaySimilarDocumentNumber = ref(
  Math.min(
    props.userDocument.similar_documents?.length ?? 0,
    SIMILAR_DOCUMENTS_INITIAL_NUMBER,
  ),
);

const displaySimilarDocuments = computed(
  () =>
    props.userDocument.similar_documents?.slice(
      0,
      displaySimilarDocumentNumber.value,
    ) ?? [],
);

/**
 * デバッグ用の判定項目
 *
 * dev環境かつ、クエリパラメータに該当の項目がある場合に、
 * ストレージ名 (box, share_point, Anewsストレージ) を表示する
 */
const showStorageType = computed(() => {
  if (!isDev) return false;

  return route.query?.showStorageType === 'true';
});

const userDocumentViewInfo = (
  viewType: UserDocumentViewType,
): UserDocumentViewInfo => ({
  page: {
    name: 'user_document_search',
  },
  feature: 'user_document_search',
  rank: props.rankInWholeFeed,
  user_document: { id: props.userDocument.id },
  contents_context: props.contentsContext as Extract<
    ContentsContext,
    { event_name: 'user_document_search' }
  >,
  view_type: viewType,
});
</script>

<template>
  <div class="user-document-card">
    <div class="user-document-representative-document">
      <div class="user-document-left">
        <div class="user-document-data-source-info c-text-regular--xs">
          <div class="user-document-page-number">
            <div>
              <DgrIcon :name="fileTypeIcon" />
            </div>
            <div class="spacing-02"></div>
            <div class="page-number">
              ページ：{{ userDocument.page_number }}
            </div>
          </div>
          <div class="spacing-04"></div>
          <component
            :is="userDocument.folder_url ? 'a' : 'span'"
            :href="userDocument.folder_url"
            target="_blank"
            rel="noopener noreferrer"
            class="user-document-data-source-name"
            @click="trackingUserDocumentEvent('user_document_folder_view')"
          >
            <DgrIcon :keep-fill="false" name="folder" class="folder-icon" />
            <div class="spacing-04"></div>
            <div class="user-document-data-source-name-text">
              {{ folderName }}
            </div>
          </component>
        </div>
        <div class="spacing-04"></div>
        <div class="user-document-title c-title-bold--xm">
          <a
            :href="documentUrl"
            target="_blank"
            rel="noopener noreferrer"
            @click="clickUserDocumentTitle()"
          >
            {{ userDocument.title }}
          </a>
        </div>
        <div class="spacing-04"></div>
        <div class="document-sentence-display">
          <DocumentSentenceDisplay
            :text="userDocument.chunk_text"
            :highlight-keywords="highlightKeywords"
            :line-clamp="2"
          ></DocumentSentenceDisplay>
        </div>
        <div class="spacing-04"></div>
        <div class="user-document-update-info">
          <div class="c-text-regular--s">更新日：{{ modifiedAt }}</div>
          <div class="c-text-regular--s">
            更新者：{{ userDocument.last_updated_by }}
          </div>
          <div class="c-text-regular--s" v-if="showStorageType">
            ストレージ: {{ getStorageType(userDocument.url) }}
          </div>
        </div>
        <div class="action-buttons">
          <UserDocumentOpenPreviewModalButton
            :id="userDocument.id"
            :title="userDocument.title"
            :target-page="userDocument.page_number"
            :file-total-pages="userDocument.file_total_pages"
            :preview-image-path-template="
              userDocument.preview_image_path_template
            "
            :user-document-view-info="userDocumentViewInfo('preview')"
          >
          </UserDocumentOpenPreviewModalButton>
          <button
            class="survey-by-spcified-file-button small"
            @click="emit('select-file', userDocument.id)"
          >
            <DgrIcon
              name="sparkles-fill"
              size="small"
              :keep-fill="false"
              class="gray-sparkles"
            />
            このファイルから要約を生成
          </button>
        </div>
      </div>
      <div>
        <a
          :href="documentUrl"
          target="_blank"
          rel="noopener noreferrer"
          @click="clickUserDocumentTitle()"
        >
          <img
            class="user-document-thumbnail"
            loading="lazy"
            :src="imageUrl"
            @error="imageErrorHandler($event, '3to1')"
          />
        </a>
      </div>
    </div>
    <div
      class="user-document-similar-documents"
      v-if="
        userDocument.similar_documents &&
        userDocument.similar_documents.length > 0
      "
    >
      <div class="c-title-bold--s">
        同一ページをもつ情報
        <span class="similar-documents-count c-text-regular--s"
          >・{{ userDocument.similar_documents.length }}件</span
        >
      </div>
      <UserDocumentSimilarDocumentCard
        :user-document-similar-document="similarDocument"
        :parent-user-document="userDocument"
        :parent-rank="rankInWholeFeed"
        :rank-in-parent="index + 1"
        :contents-context="props.contentsContext"
        v-for="(similarDocument, index) in displaySimilarDocuments"
        :key="similarDocument.id"
        :class="{
          'bottom-border': index !== userDocument.similar_documents.length - 1,
        }"
        @select-file="emit('select-file', similarDocument.id)"
      ></UserDocumentSimilarDocumentCard>
      <button
        class="c-btn c-btn--small show-more-button"
        @click="showMoreSimilarDocuments"
        v-if="
          displaySimilarDocumentNumber < userDocument.similar_documents.length
        "
      >
        さらに表示
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.user-document-card {
  width: 100%;
  display: flex;
  flex-direction: column;
  background-color: white;
  padding: 16px;
  column-gap: 16px;
  box-sizing: border-box;
  border: 1px solid $color-gray400;
  border-radius: 4px;

  .user-document-representative-document {
    display: flex;
    .user-document-left {
      width: 100%;
      .user-document-update-info {
        display: flex;
        color: $color-gray800;
        font-weight: 400;
        column-gap: 8px;
        padding: 4px 0;
        .user-document-page {
          border: 1px solid $color-gray800;
          border-radius: 2px;
          padding: 0 2px;
        }
      }
    }
    .user-document-thumbnail {
      width: 150px;
      height: 150px;
      object-fit: contain;
      font-weight: 400;
    }
    .user-document-data-source-info {
      padding: 4px 0;
      color: $color-gray800;
      font-weight: 500;
      display: flex;
      align-items: center;
      .user-document-page-number {
        display: flex;
        align-items: center;
        border-right: 1px solid $color-gray400;
        padding-right: 10px;
        .page-number {
          white-space: nowrap;
        }
      }
      .user-document-data-source-name {
        display: flex;
        align-items: center;
        .folder-icon {
          color: $color-gray600;
        }
        .user-document-data-source-name-text {
          overflow: hidden;
          display: -webkit-box;
          text-overflow: ellipsis;
          -webkit-box-orient: vertical;
          line-clamp: 1;
          -webkit-line-clamp: 1;
        }
        &a:hover {
          .user-document-data-source-name-text {
            text-decoration: underline;
          }
        }
      }
    }
    .user-document-title {
      overflow: hidden;
      display: -webkit-box;
      text-overflow: ellipsis;
      -webkit-box-orient: vertical;
      line-clamp: 2;
      -webkit-line-clamp: 2;
      word-break: break-all;
      font-weight: 500;
      a:hover {
        text-decoration: underline;
      }
    }
  }

  .document-sentence-display {
    border-left: 1px solid $color-gray400;
    padding-left: 13px;
  }

  .action-buttons {
    display: flex;
    gap: 8px;
    margin-top: 8px;
  }

  .survey-by-spcified-file-button {
    border: none;
    background-color: $color-white;
    padding: 5px 16px;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 4px;
    font-size: 14px;
    &:hover {
      background: $color-gray200;
    }
  }

  .gray-sparkles {
    flex-shrink: 0;
    color: $color-gray1000;
  }

  .user-document-similar-documents {
    border-top: 1px solid $color-gray400;
    margin-top: 8px;
    padding-top: 8px;
    .similar-documents-count {
      color: $color-gray800;
    }
    .bottom-border {
      border-bottom: 1px solid $color-gray400;
    }
    .show-more-button {
      margin: 8px auto 0 auto;
      width: auto;
      padding: 0 8px;
      background-color: white;
      border: 1px solid $color-green600;
      color: $color-green600;
    }
  }
}
</style>
